import React, { memo, useCallback, useState } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { ChatInputBar } from '@/components/residents/marketplace/shared';
import type { ChatInputTheme } from '@/components/residents/marketplace/shared';
import { SEMANTIC_COLORS, PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { CORK_BOARD_INPUT_LIMITS } from '@/constants/residents/corkBoardConstants';

const CORK_BOARD_INPUT_THEME: ChatInputTheme = {
  backgroundColor: PRIMITIVE_COLORS.white5,
  textColor: SEMANTIC_COLORS.text.primary,
  placeholderColor: SEMANTIC_COLORS.text.muted,
  iconColor: SEMANTIC_COLORS.text.muted,
  accentColor: SEMANTIC_COLORS.interactive.primary,
  previewBackgroundColor: PRIMITIVE_COLORS.gray850,
};

interface ReplyInputProps {
  isLocked: boolean;
  onSubmit: (body: string) => Promise<boolean>;
  isSubmitting: boolean;
}

export const ReplyInput: React.FC<ReplyInputProps> = memo(({ isLocked, onSubmit, isSubmitting }) => {
  const [text, setText] = useState('');

  const handleSend = useCallback(async () => {
    const trimmed = text.trim();
    if (!trimmed || isSubmitting) return;
    const success = await onSubmit(trimmed);
    if (success) setText('');
  }, [text, isSubmitting, onSubmit]);

  if (isLocked) {
    return (
      <View
        style={styles.lockedBanner}
        accessibilityRole="alert"
        accessibilityLabel="This thread is locked. Replies are disabled."
      >
        <Text style={styles.lockedText}>This thread is locked. Replies are disabled.</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <ChatInputBar
        value={text}
        onChangeText={setText}
        onSend={handleSend}
        placeholder="Write a reply..."
        maxLength={CORK_BOARD_INPUT_LIMITS.bodyMax}
        isSending={isSubmitting}
        theme={CORK_BOARD_INPUT_THEME}
        sendButtonText="Reply"
      />
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    borderTopWidth: 1,
    borderTopColor: PRIMITIVE_COLORS.gray800,
    backgroundColor: SEMANTIC_COLORS.background.secondary,
  },
  lockedBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderTopWidth: 1,
    borderTopColor: PRIMITIVE_COLORS.gray800,
    backgroundColor: SEMANTIC_COLORS.background.secondary,
  },
  lockedText: {
    fontSize: 13,
    color: SEMANTIC_COLORS.text.muted,
    fontStyle: 'italic',
  },
});
